import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  TextField,
  Popper,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
} from "@mui/material";
import { styled } from "@mui/system";
import { useSelector } from "react-redux";
import apiClient from "../../../services/apiClient";
import formatLanguage from "../../../utils/formatLanguage";
import { useChat } from "./ChatProvider";
import PersonMentionPopup from "./PersonMentionPopup";
// import SuggestedQuestionDropdown from "./SuggestedQuestionDropdown";
// import { SUGGESTED_QUESTIONS } from "./suggestedQuestions";
import { ChatModeToggle } from "./components/ChatModeToggle";
import { InputLanguageToggle } from "./components/InputLanguageToggle";
import { ChatSendControls } from "./components/ChatSendControls";
import { RecordingStopButton } from "./components/RecordingStopButton";
import { ChatInputLanguageManager } from "./managers/ChatInputLanguageManager";
import "./index.css";

const InputContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  padding: "16px 24px",
  backgroundColor: "#f1f3f4",
  width: "100%",
  marginBottom: "0",
  borderTop: "1px solid rgba(24, 116, 210, 0.12)",
  gap: "12px",
  "@media (max-width: 768px)": {
    padding: "12px 20px",
    gap: "10px",
  },
  "@media (max-width: 600px)": {
    padding: "10px 16px",
    gap: "8px",
    flexDirection: "column",
    alignItems: "stretch",
  },
  "@media (max-width: 480px)": {
    padding: "8px 12px",
    gap: "8px",
    flexDirection: "column",
    alignItems: "stretch",
  },
});

const StyledSendButton = styled(IconButton)({
  backgroundColor: "rgba(24, 116, 210, 1)",
  color: "#FFFFFF",
  width: "44px",
  height: "44px",
  borderRadius: "12px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  boxShadow: "0 4px 12px rgba(24, 116, 210, 0.3)",
  border: "1px solid rgba(24, 116, 210, 0.2)",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  flexShrink: 0,
  "&:hover": {
    backgroundColor: "rgba(24, 116, 210, 0.9)",
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(24, 116, 210, 0.4)",
  },
  "&:disabled": {
    backgroundColor: "rgba(24, 116, 210, 0.3)",
    color: "rgba(255, 255, 255, 0.6)",
    transform: "none",
    boxShadow: "0 2px 8px rgba(24, 116, 210, 0.2)",
  },
  "@media (max-width: 600px)": {
    width: "40px",
    height: "40px",
  },
  "@media (max-width: 480px)": {
    width: "36px",
    height: "36px",
  },
});

function ChatInput({ onSend, setLoading, loading }) {
  const { selectedLanguage } = useSelector((state) => state.language);
  const languageManager = useMemo(() => new ChatInputLanguageManager(), []);
  const {
    chatMode,
    setChatMode,
    setChatMessages,
    setConversationId,
    setDeepHistoryId,
    setPlannerHistoryId,
    personsList,
    selectedPerson,
    setSelectedPerson,
    isServiceBookMode,
  } = useChat();
  const [message, setMessage] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState(0);
  // const [suggestedQuestionValue, setSuggestedQuestionValue] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const skipInitialGlobalLanguageSync = useRef(true);
  const userManuallyChanged = useRef(false);
  const hasPastedContentRef = useRef(false);
  const [inputLanguageMode, setInputLanguageMode] = useState("MA");
  const handleRecord = () => setIsRecording((prev) => !prev);

  // @ mention detection for Service Book mode
  const mentionMatch = isServiceBookMode ? message.match(/@([^@]*)$/) : null;
  const showMentionPopup = mentionMatch !== null && personsList.length > 0;
  const mentionSearchText = mentionMatch ? mentionMatch[1] : "";

  const handlePersonSelect = (person) => {
    setSelectedPerson(person);
    setMessage((prev) => prev.replace(/@[^@]*$/, "").trimEnd());
  };

  useEffect(() => {
    if (!selectedLanguage) return;
    if (skipInitialGlobalLanguageSync.current) {
      skipInitialGlobalLanguageSync.current = false;
      return;
    }
    if (!userManuallyChanged.current) {
      setInputLanguageMode(
        languageManager.getInputLanguageMode(selectedLanguage),
      );
    }
  }, [selectedLanguage, languageManager]);

  useEffect(() => {
    const fetchSuggestions = async () => {
      const match = message.match(/(\S+)$/);
      const lastToken = match ? match[0] : "";

      if (
        (inputLanguageMode === "MA" || inputLanguageMode === "HI" || inputLanguageMode === "GJ") &&
        lastToken.length > 0 &&
        !hasPastedContentRef.current
      ) {
        try {
          const languageCode =
            languageManager.getLanguageCode(inputLanguageMode);
          const response = await apiClient.post(`/docs_panel/transliterate/`, {
            text: lastToken,
            language: formatLanguage(languageCode),
          });
          setSuggestions(response.data);
          setSelectedSuggestion(0);
        } catch (error) {
          console.error("Error fetching suggestions:", error);
        }
      } else {
        setSuggestions([]);
      }
    };
    fetchSuggestions();
  }, [message, inputLanguageMode]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setSuggestions([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSendClick = () => {
    if (message.trim() !== "") {
      const languageToSend = languageManager.getLanguageCode(inputLanguageMode);
      onSend(message, languageToSend);
      setMessage("");
      setSuggestions([]);
      if (isServiceBookMode && selectedPerson) {
        setSelectedPerson(null);
      }
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSendClick();
    }
  };

  const handleInputChange = (e) => {
    hasPastedContentRef.current = false;
    setMessage(e.target.value);
    setAnchorEl(inputRef.current);
  };

  const handleInputPaste = () => {
    hasPastedContentRef.current = true;
    setSuggestions([]);
  };

  const handleSuggestionClick = (suggestion) => {
    setMessage((prev) => {
      const match = prev.match(/(\S+)$/);
      if (match) {
        return prev.substring(0, match.index) + suggestion + " ";
      }
      return prev + suggestion + " ";
    });
    setSuggestions([]);
  };

  const handleKeyDown = (e) => {
    // Only handle space key for suggestions if in MA, HI, or GJ mode (not EN)
    if (
      (inputLanguageMode === "MA" || inputLanguageMode === "HI" || inputLanguageMode === "GJ") &&
      e.key === " " &&
      !hasPastedContentRef.current &&
      message.trim().length > 0 &&
      suggestions.length > 0 &&
      selectedSuggestion >= 0 &&
      selectedSuggestion < suggestions.length
    ) {
      e.preventDefault();
      const selectedText = suggestions[selectedSuggestion];
      handleSuggestionClick(selectedText);
      return;
    }

    if (e.key === "ArrowDown") {
      setSelectedSuggestion(
        selectedSuggestion < suggestions.length - 1
          ? selectedSuggestion + 1
          : 0,
      );
    }
    if (e.key === "ArrowUp") {
      setSelectedSuggestion(
        selectedSuggestion > 0
          ? selectedSuggestion - 1
          : suggestions.length - 1,
      );
    }
  };

  const handleModeChange = (_event, nextMode) => {
    if (!nextMode || nextMode === chatMode) return;
    setChatMode(nextMode);
    setChatMessages([]);
    setConversationId(null);
    setDeepHistoryId(null);
    setPlannerHistoryId(null);
    setSuggestions([]);
    setMessage("");
    if (selectedPerson) {
      setSelectedPerson(null);
    }
  };

  const handleInputLanguageModeChange = (_event, nextMode) => {
    if (nextMode) {
      setInputLanguageMode(nextMode);
      setSuggestions([]); // Clear suggestions when switching modes
      userManuallyChanged.current = true; // Mark that user manually changed the language
    }
  };

  return (
    <Box
      bgcolor={"#f1f3f4"}
      display={"flex"}
      alignItems={"center"}
      flexDirection={"column"}
      justifyContent={"flex-end"}
      sx={{
        pb: "0",
        mt: "auto",
        borderBottomLeftRadius: "0",
        borderBottomRightRadius: "0",
        position: "static",
        minHeight: "fit-content",
        backgroundColor: "#f1f3f4",
        borderTop: "1px solid rgba(24, 116, 210, 0.12)",
      }}
    >
      <InputContainer>
        {/* Desktop: Chat mode selector on left, input in middle, buttons on right */}
        {/* Mobile: Input on top, then bottom row with mode selector left and buttons right */}
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            flexDirection: "column",
            alignItems: "center",
            mr: 1,
            flexShrink: 0,
            gap: 0.75,
          }}
        >
          <ChatModeToggle value={chatMode} onChange={handleModeChange} />
          <InputLanguageToggle
            value={inputLanguageMode}
            onChange={handleInputLanguageModeChange}
            buttonMinWidth={40}
            buttonPaddingX={1}
            buttonFontSize="0.75rem"
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flex: { xs: "0 0 auto", sm: "1 1 auto" },
            minWidth: 0,
            mr: { xs: 0, sm: 1 },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {/* <SuggestedQuestionDropdown
            questions={SUGGESTED_QUESTIONS}
            value={suggestedQuestionValue}
            onChange={(q) => {
              setMessage(q);
              setSuggestedQuestionValue("");
            }}
          /> */}
          <TextField
            ref={inputRef}
            value={message}
            onChange={handleInputChange}
            onPaste={handleInputPaste}
            onKeyPress={handleKeyPress}
            onKeyDown={handleKeyDown}
            variant="outlined"
            placeholder="Type a message..."
            fullWidth
            multiline
            maxRows={4}
            sx={{
              width: "100%",
            }}
            InputProps={{
              startAdornment:
                isServiceBookMode && selectedPerson ? (
                  <Chip
                    label={selectedPerson.person_name}
                    onDelete={() => setSelectedPerson(null)}
                    size="small"
                    sx={{
                      mr: 1,
                      flexShrink: 0,
                      backgroundColor: "rgba(24, 116, 210, 0.1)",
                      color: "rgba(24, 116, 210, 1)",
                      fontWeight: 500,
                      fontSize: "0.75rem",
                      borderRadius: "8px",
                      border: "1px solid rgba(24, 116, 210, 0.2)",
                      "& .MuiChip-deleteIcon": {
                        color: "rgba(24, 116, 210, 0.6)",
                        "&:hover": { color: "rgba(24, 116, 210, 1)" },
                      },
                    }}
                  />
                ) : undefined,
              style: {
                borderRadius: "12px",
                backgroundColor: "#ffffff",
                fontSize: "0.95rem",
                padding: "8px 16px",
              },
              sx: {
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid rgba(24, 116, 210, 0.16)",
                  borderRadius: "12px",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid rgba(24, 116, 210, 0.28)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "2px solid rgba(24, 116, 210, 1)",
                },
                "& .MuiInputBase-input": {
                  padding: { xs: "10px 14px", sm: "12px 16px" },
                  fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
                  lineHeight: 1.5,
                  color: "#2c3e50",
                },
              },
            }}
          />
        </Box>
        <Popper
          open={Boolean(anchorEl) && suggestions.length > 0}
          anchorEl={anchorEl}
          placement="bottom-start"
          ref={suggestionsRef}
        >
          <Paper elevation={3}>
            <List>
              {suggestions.map((suggestion, index) => (
                <ListItem
                  style={{
                    cursor: "pointer",
                    backgroundColor:
                      selectedSuggestion === index ? "#f0f0f0" : "transparent",
                  }}
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <ListItemText primary={suggestion} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Popper>
        {showMentionPopup && (
          <PersonMentionPopup
            anchorEl={inputRef.current}
            persons={personsList}
            searchText={mentionSearchText}
            onSelect={handlePersonSelect}
          />
        )}
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
          }}
        >
          <ChatSendControls
            loading={loading}
            onSendClick={handleSendClick}
            onPauseClick={setLoading}
            StyledSendButton={StyledSendButton}
            messageSetter={setMessage}
            isRecording={isRecording}
            setIsRecording={setIsRecording}
            handleRecord={handleRecord}
            transcriptionLanguage={languageManager.getLanguageCode(
              inputLanguageMode,
            )}
          />
        </Box>

        {/* Mobile: Bottom row with mode selector on left and buttons on right */}
        <Box
          sx={{
            display: { xs: "flex", sm: "none" },
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            gap: 1,
            mt: 0.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              gap: 1,
            }}
          >
            <ChatModeToggle
              value={chatMode}
              onChange={handleModeChange}
              iconSize={16}
              buttonPaddingX={1}
            />
            <InputLanguageToggle
              value={inputLanguageMode}
              onChange={handleInputLanguageModeChange}
              buttonMinWidth={44}
              buttonPaddingX={1.25}
              buttonFontSize="0.8125rem"
            />
          </Box>
          <ChatSendControls
            compact
            loading={loading}
            onSendClick={handleSendClick}
            onPauseClick={setLoading}
            StyledSendButton={StyledSendButton}
            messageSetter={setMessage}
            isRecording={isRecording}
            setIsRecording={setIsRecording}
            handleRecord={handleRecord}
            transcriptionLanguage={languageManager.getLanguageCode(
              inputLanguageMode,
            )}
          />
        </Box>
      </InputContainer>
      <RecordingStopButton
        isRecording={isRecording}
        loading={loading}
        onStop={handleRecord}
      />
    </Box>
  );
}

export default ChatInput;
